import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { useTranslation } from 'react-i18next';
import { useBranding } from './branding';
import { buildTheme } from './theme';

/**
 * Wires language → direction → emotion cache. `stylis-plugin-rtl` flips the
 * generated CSS (margins, paddings, floats), which is what makes the whole UI
 * mirror rather than just the text.
 */
export function DirectionProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const isRtl = i18n.language.startsWith('ar');
  const direction = isRtl ? 'rtl' : 'ltr';

  const cache = useMemo(
    () =>
      createCache({
        key: isRtl ? 'erb-rtl' : 'erb-ltr',
        stylisPlugins: isRtl ? [prefixer, rtlPlugin] : [prefixer],
      }),
    [isRtl],
  );

  // The customer's accent, where one is set: the palette is rebuilt rather
  // than patched, so every derived shade follows it.
  const { color } = useBranding();
  const theme = useMemo(() => buildTheme(direction, color), [direction, color]);

  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', i18n.language);
  }, [direction, i18n.language]);

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </CacheProvider>
  );
}
